import React from "react";
import { Dropdown, Form, Image, InputGroup, Table } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faBookOpen,
  faClock,
  faFileLines,
  faFilePowerpoint,
  faHeadphones,
  faLayerGroup,
  faPencil,
  faRobot,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import Flag from "react-world-flags";
import PageHeading from "./PageHeading";
import CustomPagination from "./Pagination";
import avatar from "../../assets/avatar.png";
import { getFlagCode } from "../../utils/languageFlag";

// CEFR equivalents shown alongside the internal difficulty labels.
export const CEFR_LABELS = {
  Beginner: "A1",
  Elementary: "A2",
  Intermediate: "B1",
  Advanced: "B2",
  Expert: "C1–C2",
};

// "Beginner (A1)" style label; falls back to the plain (translated) level.
export const levelLabel = (level, t) => {
  if (!level) return level;
  const cefr = CEFR_LABELS[level];
  return cefr ? `${t(level)} (${cefr})` : t(level);
};

// "A1 · Elementary" style label (CEFR code first) used in the Level column.
export const levelLabelCefrFirst = (level, t) => {
  if (!level) return level;
  const cefr = CEFR_LABELS[level];
  return cefr ? `${cefr} · ${t(level)}` : t(level);
};

// Colour of the small dot beside the level, banded by CEFR.
export const levelDotColor = (level) => {
  switch (level) {
    case "Beginner":
    case "Elementary":
      return "#22C55E"; // green (A1–A2)
    case "Intermediate":
      return "#3B82F6"; // blue (B1)
    case "Advanced":
      return "#6366F1"; // indigo (B2)
    case "Expert":
      return "#EF4444"; // red (C1–C2)
    default:
      return "#B0BEC5";
  }
};

// Per-type accent colours, shared by the thumbnail tile, the Type badge and the
// type tabs so everything about a type shares one colour.
export const typeColors = {
  reading: { bg: "#FDF3E7", color: "#E0872B", icon: faBookOpen },
  writing: { bg: "#FDECEC", color: "#DE494B", icon: faPencil },
  listening: { bg: "#E8F6EE", color: "#2E9E5B", icon: faHeadphones },
  speaking: { bg: "#E6F7F7", color: "#0F9D9D", icon: faBookOpen },
  grammar: { bg: "#EAF1FE", color: "#3B82F6", icon: faBookOpen },
  vocabulary: { bg: "#F1ECFD", color: "#8B5CF6", icon: faBook },
  presentations: { bg: "#FDECF3", color: "#DB2777", icon: faFilePowerpoint },
};

export const exerciseTypeOptions = ["All", "reading", "writing", "listening"];
export const difficultyOptions = [
  "All",
  "Beginner",
  "Elementary",
  "Intermediate",
  "Advanced",
  "Expert",
];

// Reusable Level filter dropdown for exercise pages. `onChange` receives the
// chosen level ("All" for no filter).
export const DifficultyFilter = ({ selected, onChange, t }) => (
  <Dropdown>
    <Dropdown.Toggle variant="light" id="exercise-difficulty">
      {selected && selected !== "All" ? levelLabel(selected, t) : t("All Levels")}
    </Dropdown.Toggle>
    <Dropdown.Menu>
      {difficultyOptions.map((level) => (
        <Dropdown.Item key={level} onClick={() => onChange(level)}>
          {level === "All" ? t("All Levels") : levelLabel(level, t)}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);
export const sortOptions = [
  "A to Z",
  "Z to A",
  "Difficulty Level",
  "Latest",
  "Oldest",
];

export const getLevelBackgroundColor = (level) => {
  switch (level) {
    case "Elementary":
      return "#C4C4C4";
    case "Intermediate":
      return "#4DA9F4";
    case "Beginner":
      return "#0FE133";
    case "Advanced":
      return "#EB3232";
    case "Expert":
      return "#408c40";
    default:
      return "#B0BEC5";
  }
};

// Icon shown in the type tabs.
const tabIcon = (type) => {
  const key = String(type || "").toLowerCase();
  if (key === "reading") return faBookOpen;
  if (key === "writing") return faPencil;
  if (key === "listening") return faHeadphones;
  if (key === "vocabulary") return faBook;
  if (key === "presentations") return faFilePowerpoint;
  return faLayerGroup;
};

// Exercises carry no stored image, so key a tinted icon tile off the type.
const ExerciseThumb = ({ type }) => {
  const key = String(type || "").toLowerCase();
  const s = typeColors[key] || { bg: "#F2F2F5", color: "#8c8c8c", icon: faFileLines };
  return (
    <div className="exercise-thumb" style={{ background: s.bg, color: s.color }}>
      <FontAwesomeIcon icon={s.icon} />
    </div>
  );
};

// "45 min" / "1 h 15 min" — minutes alone get unreadable past an hour.
export const formatDuration = (minutes, t) => {
  if (!minutes || minutes < 1) return "-";
  if (minutes < 60) return `${minutes} ${t("min")}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest
    ? `${hours} ${t("h")} ${rest} ${t("min")}`
    : `${hours} ${t("h")}`;
};

/**
 * Column definitions shared by the exercise list pages. Each page picks the
 * ones it needs via the `columns` prop (see COLUMN_KEYS below); anything
 * page-specific is passed in as a custom column object instead.
 */
const buildColumns = (t, onTitleClick) => ({
  title: {
    key: "title",
    label: "Title",
    thClassName: "py-3 ps-3 fw-semibold",
    tdClassName: "py-3 ps-3",
    skeleton: <Skeleton width="70%" />,
    render: (ex) => (
      <div className="d-flex align-items-center gap-2">
        <ExerciseThumb type={ex.exerciseType} />
        <div>
          <div
            className="fw-bold exercise-title-link"
            style={{ fontSize: "14px", cursor: "pointer", width: "fit-content" }}
            onClick={() => onTitleClick(ex._id)}
          >
            {ex.title}
          </div>
          <div className="d-flex gap-2 flex-wrap mt-1">
            {ex?.initialPrompt?.textType && (
              <span className="text-muted" style={{ fontSize: "12px" }}>
                {t(ex.initialPrompt.textType)}
              </span>
            )}
            {ex.creationMethod === "AI" && (
              <span className="text-muted" style={{ fontSize: "12px" }}>
                <FontAwesomeIcon icon={faRobot} className="me-1" />
                {t("AI Generated")}
              </span>
            )}
            {ex?.hasMultipleVariants && (
              <span className="text-muted" style={{ fontSize: "12px" }}>
                <FontAwesomeIcon icon={faLayerGroup} className="me-1" />
                {t("Multiple Exercises")}
              </span>
            )}
          </div>
        </div>
      </div>
    ),
  },

  type: {
    key: "type",
    label: "Type",
    skeleton: <Skeleton width={70} />,
    render: (ex) => {
      const key = String(ex.exerciseType || "").toLowerCase();
      if (!key) return "-";
      const c = typeColors[key] || { bg: "#F2F2F5", color: "#8c8c8c" };
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 12px",
            borderRadius: "30px",
            backgroundColor: "#fff",
            border: `1px solid ${c.color}`,
            color: c.color,
            fontSize: "12px",
            fontWeight: 600,
            textTransform: "capitalize",
            whiteSpace: "nowrap",
          }}
        >
          {ex.exerciseType}
        </span>
      );
    },
  },

  subject: {
    key: "subject",
    label: "Subject",
    skeleton: <Skeleton width={60} />,
    tdStyle: { fontSize: "14px" },
    render: (ex) =>
      ex?.subject?.title ? (
        <Flag
          code={getFlagCode(ex.subject.title)}
          title={ex.subject.title}
          style={{ width: 24, height: 24, borderRadius: 3 }}
        />
      ) : (
        "-"
      ),
  },

  level: {
    key: "level",
    label: "Level",
    skeleton: <Skeleton width={70} />,
    render: (ex) => (
      <span
        className="d-inline-flex align-items-center gap-2"
        style={{ fontSize: "13px", whiteSpace: "nowrap" }}
      >
        <span
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: ex?.hasDifficultyVariants
              ? "#8C8C9A"
              : levelDotColor(ex.difficultyLevel),
          }}
        />
        {ex?.hasDifficultyVariants
          ? t("Multiple Difficulty Levels")
          : levelLabelCefrFirst(ex.difficultyLevel, t)}
      </span>
    ),
  },

  author: {
    key: "author",
    label: "Author",
    skeleton: <Skeleton width={90} />,
    render: (ex) => (
      <div className="d-flex gap-1 align-items-center">
        <Image
          src={ex?.originalAuthor?.imageUrl?.url || avatar}
          className="shadow-md"
          height={18}
          width={18}
          style={{ borderRadius: "60px" }}
        />
        <span className="text-muted" style={{ fontSize: "12px", fontWeight: 600 }}>
          {ex?.originalAuthor?.fullName || ex?.author?.fullName}
        </span>
      </div>
    ),
  },

  duration: {
    key: "duration",
    label: "Est. time",
    skeleton: <Skeleton width={55} />,
    render: (ex) =>
      ex.estimatedMinutes ? (
        <span
          className="d-inline-flex align-items-center gap-1 text-muted"
          style={{ fontSize: "13px", whiteSpace: "nowrap" }}
          title={t("Estimated time to complete")}
        >
          <FontAwesomeIcon icon={faClock} style={{ fontSize: "12px" }} />
          {formatDuration(ex.estimatedMinutes, t)}
        </span>
      ) : (
        <span className="text-muted" style={{ fontSize: "13px" }}>
          -
        </span>
      ),
  },

  updatedAt: {
    key: "updatedAt",
    label: "Last updated",
    skeleton: <Skeleton width={70} />,
    tdClassName: "py-3 text-muted",
    tdStyle: { fontSize: "13px", whiteSpace: "nowrap" },
    render: (ex, { formatRelativeTime }) =>
      formatRelativeTime(ex.updatedAt || ex.createdAt),
  },
});

export const COLUMN_KEYS = [
  "title",
  "type",
  "subject",
  "level",
  "duration",
  "author",
  "updatedAt",
];

/**
 * Shared shell for the exercise list pages (Library, My Exercises, Favourites).
 *
 * Renders the header (title + subject/sort/search), the type tabs, an optional
 * status card row, the results table and the pagination footer. Pages supply
 * their own data, filter state and row actions.
 */
const ExerciseListPage = ({
  // Header
  heading,
  subtitle,
  headerExtra,
  // Data
  exercises = [],
  loading,
  emptyState,
  // Filters
  subjects = [],
  selectedSubject,
  selectedType,
  selectedSort,
  search,
  searchPlaceholder = "Search exercises...",
  onSearchChange,
  onSubjectChange,
  onTypeChange,
  onSortChange,
  onSearchSubmit,
  // Extra filter controls rendered alongside the built-in dropdowns.
  filterExtra,
  sortOptions: sortChoices = sortOptions,
  // Type tabs are hidden when a page passes an empty list.
  typeTabs = exerciseTypeOptions,
  // "inline" (default) keeps filters in the header row; "split" moves them to
  // their own row and leaves headerExtra alone beside the title.
  filterBarLayout = "inline",
  // Optional status card row
  statusCards,
  activeStatusCard,
  onStatusCardChange,
  statusCounts,
  // Table
  columns = COLUMN_KEYS,
  onTitleClick,
  renderActions,
  rowStyle,
  onRowClick,
  // Pagination
  currentPage,
  totalPages,
  onPageChange,
  // Infinite scroll (opt-in). When `infiniteScroll` is true the pager is
  // replaced by an IntersectionObserver sentinel that calls `onLoadMore` as it
  // nears the viewport, with a "Load more" button fallback.
  infiniteScroll = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  // Rendered below the pagination footer.
  footerExtra,
  // When provided, replaces the status cards + table + pager (used to show the
  // Vocabulary / Presentations views under the same tab row). May be a node or
  // a render function `(headerSlot) => node`; the function form receives the
  // header-controls DOM node so the override can portal its own filter controls
  // up into the shared header row (keeping them level with the other tabs).
  contentOverride = null,
}) => {
  const { t } = useTranslation();
  const loadMoreRef = React.useRef(null);
  // DOM node for the override's filter controls, tracked in state so the render
  // function re-runs once the slot mounts and the portal target is available.
  const [overrideHeaderSlot, setOverrideHeaderSlot] = React.useState(null);

  // Fetch the next page as the sentinel below the table scrolls into view.
  React.useEffect(() => {
    if (!infiniteScroll) return;
    const node = loadMoreRef.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !loadingMore) {
          onLoadMore?.();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [infiniteScroll, hasMore, loading, loadingMore, onLoadMore]);

  // "2 days ago" style label for the Last updated column.
  const formatRelativeTime = (date) => {
    if (!date) return "-";
    const then = new Date(date).getTime();
    if (Number.isNaN(then)) return "-";
    const days = Math.floor((Date.now() - then) / 86400000);
    if (days < 1) return t("Today");
    if (days === 1) return t("Yesterday");
    if (days < 7) return `${days} ${t("days ago")}`;
    if (days < 30) {
      const weeks = Math.floor(days / 7);
      return `${weeks} ${weeks === 1 ? t("week ago") : t("weeks ago")}`;
    }
    const months = Math.floor(days / 30);
    return `${months} ${months === 1 ? t("month ago") : t("months ago")}`;
  };

  const registry = buildColumns(t, (id) => onTitleClick(id));
  // Columns may be a registry key or a full custom column object.
  const resolved = columns.map((c) => (typeof c === "string" ? registry[c] : c));
  const colSpan = resolved.length + (renderActions ? 1 : 0);

  // `split` puts the primary action beside the title and drops the filters onto
  // their own row underneath; the default keeps everything on one line.
  const splitHeader = filterBarLayout === "split";

  const subjectFilter = onSubjectChange && (
    <Dropdown>
      <Dropdown.Toggle variant="light" id={`${heading}-subject`}>
        {t(selectedSubject) || t("Select Subject")}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => onSubjectChange("All")}>
          {t("All")}
        </Dropdown.Item>
        {subjects?.map((item) => (
          <Dropdown.Item
            key={item.value}
            onClick={() => onSubjectChange(item.label)}
          >
            {t(item.label)}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  const sortFilter = (
    <Dropdown>
      <Dropdown.Toggle variant="light" id={`${heading}-sort`}>
        {splitHeader
          ? `${t("Sort by")}: ${t(selectedSort) || t(sortChoices[0])}`
          : t(selectedSort) || t("Select Sort")}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {sortChoices.map((item) => (
          <Dropdown.Item key={item} onClick={() => onSortChange(item)}>
            {t(item)}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  const searchField = (
          <Form.Group className="position-relative mb-0" style={{ width: "240px" }}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder={t(searchPlaceholder)}
                className="px-3 py-2 border-end-0"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onSearchSubmit();
                  }
                }}
                style={{
                  borderTopLeftRadius: "30px",
                  borderBottomLeftRadius: "30px",
                  borderColor: "#e3e3e6",
                  borderRight: 0,
                  fontSize: "14px",
                }}
              />
              <InputGroup.Text
                className="bg-white"
                onClick={onSearchSubmit}
                style={{
                  cursor: "pointer",
                  borderTopRightRadius: "30px",
                  borderBottomRightRadius: "30px",
                  borderColor: "#e3e3e6",
                }}
              >
                <FontAwesomeIcon icon={faSearch} style={{ color: "#8c8c8c" }} />
              </InputGroup.Text>
            </InputGroup>
          </Form.Group>
  );

  return (
    <div className="exercise-page">
      {/* Header: title, inline filters, search */}
      <div className="exercise-header">
        <div>
          <PageHeading heading={heading} />
          {subtitle && <p className="exercise-header-subtitle">{t(subtitle)}</p>}
        </div>

        {contentOverride ? (
          // Slot the override (Vocabulary / Presentations) portals its filter
          // controls into, so they sit in the shared header row like the other
          // tabs instead of under the tab bar.
          <div className="exercise-header-controls" ref={setOverrideHeaderSlot} />
        ) : splitHeader ? (
          headerExtra
        ) : (
          <div className="exercise-header-controls">
            {subjectFilter}
            {filterExtra}
            {sortFilter}
            {searchField}
            {headerExtra}
          </div>
        )}
      </div>

      {/* Filter bar on its own row */}
      {splitHeader && (
        <div className="exercise-filter-bar">
          <div className="exercise-header-controls">
            {searchField}
            {subjectFilter}
            {filterExtra}
          </div>
          <div className="exercise-header-controls">{sortFilter}</div>
        </div>
      )}

      {/* Exercise type tabs */}
      {typeTabs.length > 0 && (
      <div className="exercise-tabs">
        {typeTabs.map((item) => {
          const isActive = (selectedType || "All") === item;
          const tint = typeColors[String(item).toLowerCase()]?.color;
          return (
            <button
              key={item}
              type="button"
              className={`exercise-tab${isActive ? " active" : ""}`}
              onClick={() => onTypeChange(item)}
            >
              <FontAwesomeIcon
                icon={tabIcon(item)}
                style={tint ? { color: tint } : undefined}
              />
              {t(item)}
            </button>
          );
        })}
      </div>
      )}

      {/* When a section override (Vocabulary / Presentations) is active it
          replaces the status cards, table and pager below. A function override
          receives the header slot so it can portal its filters into the row. */}
      {typeof contentOverride === "function"
        ? contentOverride(overrideHeaderSlot)
        : contentOverride}

      {/* Optional status cards */}
      {!contentOverride && statusCards?.length > 0 && (
        <div className="exercise-status-row">
          {statusCards.map((card) => (
            <button
              key={card.key}
              type="button"
              className={`exercise-status-card${
                activeStatusCard === card.key ? " active" : ""
              }`}
              onClick={() => onStatusCardChange(card.key)}
            >
              {card.dot ? (
                <span
                  className="status-card-dot"
                  style={{ background: card.dot }}
                />
              ) : (
                card.icon && <FontAwesomeIcon icon={card.icon} />
              )}
              {t(card.label)}
              <span className="exercise-status-count">
                {statusCounts ? statusCounts[card.countKey] ?? 0 : "-"}
              </span>
            </button>
          ))}
        </div>
      )}

      {!contentOverride && (
      <div className="exercise-page-body">
        <div className="exercise-results">
          <div
            className="bg-white exercise-table-scroll"
            style={{
              borderRadius: "10px",
              boxShadow: "rgba(100, 100, 111, 0.15) 0px 4px 20px 0px",
            }}
          >
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr style={{ fontSize: "13px", color: "#8c8c8c" }}>
                  {resolved.map((col) => (
                    <th
                      key={col.key}
                      className={col.thClassName || "py-3 fw-semibold"}
                    >
                      {t(col.label)}
                    </th>
                  ))}
                  {renderActions && (
                    <th
                      className="py-3 pe-3 fw-semibold text-end"
                      // Fixed so toggling an action icon can't resize the column.
                      style={{ width: "110px" }}
                    >
                      {t("Actions")}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {resolved.map((col) => (
                        <td
                          key={col.key}
                          className={col.tdClassName || "py-3"}
                        >
                          {col.skeleton || <Skeleton width={70} />}
                        </td>
                      ))}
                      {renderActions && (
                        <td className="py-3 pe-3">
                          <Skeleton width={40} />
                        </td>
                      )}
                    </tr>
                  ))
                ) : exercises.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="text-center py-5">
                      {emptyState || (
                        <span className="text-muted">
                          {t("No exercises found")}
                        </span>
                      )}
                    </td>
                  </tr>
                ) : (
                  exercises.map((ex) => (
                    <tr
                      key={ex._id}
                      onClick={onRowClick ? () => onRowClick(ex) : undefined}
                      style={{
                        ...(rowStyle ? rowStyle(ex) : {}),
                        ...(onRowClick ? { cursor: "pointer" } : {}),
                      }}
                    >
                      {resolved.map((col) => (
                        <td
                          key={col.key}
                          className={col.tdClassName || "py-3"}
                          style={col.tdStyle}
                        >
                          {col.render(ex, { formatRelativeTime, t })}
                        </td>
                      ))}
                      {renderActions && (
                        <td className="py-3 pe-3">
                          <div className="exercise-actions">
                            {renderActions(ex)}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {infiniteScroll ? (
            <>
              <div className="exercise-footer">
                <span className="text-muted" style={{ fontSize: "13px" }}>
                  {!loading && `${exercises.length} ${t("results")}`}
                </span>
              </div>
              {/* Infinite scroll sentinel: entering the viewport loads more. */}
              {!loading && hasMore && (
                <div ref={loadMoreRef} className="text-center py-3">
                  {loadingMore ? (
                    <span className="text-secondary">{t("Loading more...")}</span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => onLoadMore?.()}
                      style={{
                        border: "1px solid #e3e3e6",
                        borderRadius: "30px",
                        padding: "7px 20px",
                        fontWeight: 500,
                      }}
                    >
                      {t("Load more")}
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="exercise-footer">
              <span className="text-muted" style={{ fontSize: "13px" }}>
                {!loading && `${exercises.length} ${t("results")}`}
              </span>
              <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                compact
                loading={loading}
              />
            </div>
          )}

          {footerExtra}
        </div>
      </div>
      )}
    </div>
  );
};

export default ExerciseListPage;
